/**
 * Invoice slice pieces — the generated billing invoices for an account, their
 * line items, and the search/sort/paging state behind the invoices table.
 *
 * Contains:
 * - invoicesInitialState / invoicesReducers — adapter state + view setters
 * - addInvoiceCases — load/edit/QuickBooks-push extra reducers
 * - invoice selectors, including selectAccountInvoicesForPeriod, which
 *   summarizes an account's invoices over a date range against an optional
 *   comparison range (per-category roll-up via summarizeCategorySubtotals)
 */
import { createSelector } from '@reduxjs/toolkit';
import { parse } from 'date-fns';
import { normalize } from 'normalizr';

import { editInvoice, loadInvoice, loadInvoices, pushQuickBooksInvoice } from '@sophocles/features/billing/api';
import { InvoiceSchema } from '@sophocles/features/billing/schema';
import { invoicesAdapter } from '@sophocles/features/billing/slices/adapters';
import { summarizeCategorySubtotals } from '@sophocles/features/billing/slices/invoiceSummary';

export const invoicesInitialState = invoicesAdapter.getInitialState({
  view: {
    search: null,
    page: 0,
    order: 'asc',
    orderBy: 'id',
  },
});

export const invoicesReducers = {
  setInvoicePage(state, action) {
    state.invoices.view.page = action.payload;
  },
  setInvoiceSearch(state, action) {
    state.invoices.view.search = action.payload;
  },
  setInvoiceOrder(state, action) {
    state.invoices.view.order = action.payload;
  },
  setInvoiceOrderBy(state, action) {
    state.invoices.view.orderBy = action.payload;
  },
};

export const addInvoiceCases = builder => {
  builder
    .addCase(loadInvoice.fulfilled, (state, action) => {
      invoicesAdapter.upsertOne(state.invoices, action.payload);
    })
    .addCase(loadInvoices.fulfilled, (state, action) => {
      if (action.payload.results.length === 0) {
        return;
      }
      const normalized = normalize(action.payload.results, [InvoiceSchema]);
      invoicesAdapter.upsertMany(state.invoices, normalized.entities.invoices);
    })
    .addCase(editInvoice.fulfilled, (state, action) => {
      invoicesAdapter.upsertOne(state.invoices, action.payload);
    })
    .addCase(pushQuickBooksInvoice.fulfilled, (state, action) => {
      action.payload.forEach(({ invoice_id, foreign_id }) => {
        const invoice = state.invoices.entities[invoice_id];
        if (invoice) {
          invoice.foreign_id = foreign_id;
        }
      });
    });
};

export const selectInvoiceEntities = state => {
  return state.billing.invoices;
};
export const { selectById: selectInvoiceById, selectAll: selectInvoices } =
  invoicesAdapter.getSelectors(selectInvoiceEntities);
export const selectInvoice = id => state => selectInvoiceById(state, id);
export const selectAccountInvoicesForPeriod = createSelector(
  [
    selectInvoices,
    (state, accountId) => accountId,
    (state, accountId, start) => start,
    (state, accountId, start, end) => end,
    (state, accountId, start, end, compareStart) => compareStart,
    (state, accountId, start, end, compareStart, compareEnd) => compareEnd,
  ],
  (invoices, accountId, start, end, compareStart, compareEnd) => {
    const startDate = parse(start, 'yyyy-MM-dd', new Date());
    const endDate = parse(end, 'yyyy-MM-dd', new Date());
    let compareInvoices = null;

    if (compareStart != null && compareEnd != null) {
      const compareStartDate = parse(compareStart, 'yyyy-MM-dd', new Date());
      const compareEndDate = parse(compareEnd, 'yyyy-MM-dd', new Date());
      compareInvoices = invoices
        .filter(invoice => {
          const invoiceDate = parse(invoice.start_date, 'yyyy-MM-dd', new Date());
          return invoice.account == accountId && invoiceDate >= compareStartDate && invoiceDate <= compareEndDate;
        })
        .map(invoice => {
          const summary = summarizeCategorySubtotals(invoice.category_subtotals);
          return { ...invoice, summary };
        })
        .reduce((acc, invoice) => {
          acc[invoice.site_group] = invoice;
          return acc;
        }, {});
    }

    return invoices
      .filter(invoice => {
        const invoiceDate = parse(invoice.start_date, 'yyyy-MM-dd', new Date());
        return invoice.account == accountId && invoiceDate >= startDate && invoiceDate <= endDate;
      })
      .map(invoice => {
        const summary = summarizeCategorySubtotals(invoice.category_subtotals);
        const items = invoice.items.map(item => {
          let price_diff = null;
          let quantity_diff = null;

          if (compareInvoices != null) {
            const compareInvoice = compareInvoices[invoice.site_group];
            if (compareInvoice != null) {
              const compareItem = compareInvoice.items.find(
                compareItem =>
                  compareItem.category.id == item.category.id && compareItem.description == item.description,
              );
              if (compareItem != null) {
                price_diff = parseFloat(item.price) - parseFloat(compareItem.price);
                quantity_diff = parseFloat(item.quantity) - parseFloat(compareItem.quantity);
              }
            }
          }

          return { ...item, price_diff, quantity_diff };
        });
        return { ...invoice, items, summary };
      })
      .sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
  },
);
export const selectInvoiceViewProperties = createSelector(selectInvoiceEntities, state => state.view);
